import { post, get, put, del } from './api'
import type { User, LoginRequest, LoginResponse, RegisterRequest } from '@/types'

// =============================================================================
// Authentication Service
// =============================================================================

export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginRequest): Promise<{ token: LoginResponse; user: User }> {
    const response = await post<LoginResponse>('/api/auth/login', {
      email: credentials.email,
      password: credentials.password,
    })

    // Store token in sessionStorage so the session ends when the browser is closed
    sessionStorage.setItem('access_token', response.access_token)

    // Fetch user profile
    const user = await this.getCurrentUser()
    sessionStorage.setItem('user', JSON.stringify(user))

    return { token: response, user }
  },

  /**
   * Check if registration is open or invite-only
   */
  async getRegistrationStatus(): Promise<{ enabled: boolean; invite_required: boolean }> {
    return get<{ enabled: boolean; invite_required: boolean }>('/api/auth/registration-status')
  },

  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<User> {
    const response = await post<User>('/api/auth/register', data)
    return response
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    return get<User>('/api/auth/me')
  },

  /**
   * Logout — revoke the token server-side (H-3), then clear local credentials.
   * The server call is best-effort: if it fails (offline), we still clear locally
   * so the user is never trapped in a half-logged-out state.
   */
  async logout(): Promise<void> {
    try {
      if (sessionStorage.getItem('access_token')) {
        await post('/api/auth/logout', {})
      }
    } catch {
      // ignore — clear locally regardless
    } finally {
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('user')
      // Older builds wrote the user object to localStorage; wipe any residue
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!sessionStorage.getItem('access_token')
  },

  /**
   * Get stored user
   */
  getStoredUser(): User | null {
    const userJson = sessionStorage.getItem('user')
    if (userJson) {
      try {
        return JSON.parse(userJson)
      } catch {
        return null
      }
    }
    return null
  },

  /**
   * Get stored token
   */
  getToken(): string | null {
    return sessionStorage.getItem('access_token')
  },

  /**
   * Update user profile
   */
  async updateProfile(data: { full_name: string; email: string }): Promise<User> {
    const response = await put<User>('/api/auth/me', data)
    // Update stored user
    sessionStorage.setItem('user', JSON.stringify(response))
    return response
  },

  /**
   * Change user password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await post<void>('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
  },

  /**
   * Request a password reset email
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return post<{ message: string }>('/api/auth/request-password-reset', { email })
  },

  /**
   * Confirm password reset with token and new password
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<{ message: string }> {
    return post<{ message: string }>('/api/auth/confirm-password-reset', {
      token,
      new_password: newPassword,
    })
  },

  /**
   * Permanently delete the current account. Requires the password for
   * re-confirmation. On success the caller must clear the session.
   */
  async deleteAccount(password: string): Promise<void> {
    await del<void>('/api/auth/me', { data: { password } })
  },

  /**
   * Refresh the access token before it expires.
   * Called proactively by the auth timer and reactively by the 401 interceptor.
   */
  async refreshToken(): Promise<string> {
    const response = await post<{ access_token: string; token_type: string; expires_in: number }>(
      '/api/auth/refresh'
    )
    sessionStorage.setItem('access_token', response.access_token)
    return response.access_token
  },
}
